'use client';

import { m, useMotionTemplate, useMotionValue, useSpring } from 'motion/react';
import { ArrowRight, Server, Zap, Code2 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import Link from 'next/link';
import Image from 'next/image';
import { MouseEvent, useRef, useEffect, useState } from 'react';

export default function Hero() {
  const { t } = useLanguage();
  const isMobile = useIsMobile();

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  const containerRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ left: number, top: number } | null>(null);

  function handleMouseEnter() {
    if (containerRef.current) {
      const { left, top } = containerRef.current.getBoundingClientRect();
      setRect({ left, top });
    }
  }

  function handleMouseMove({ clientX, clientY }: MouseEvent) {
    if (!rect) return;
    mouseX.set(clientX - rect.left);
    mouseY.set(clientY - rect.top);
  }

  return (
    <section
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      className="relative min-h-[100dvh] flex flex-col justify-center items-center pt-20 overflow-hidden text-center group"
    >
      {/* Background Static Image with JS Parallax/Pan Effect */}
      <div className="absolute inset-0 bg-[#000000] overflow-hidden">
        <m.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 opacity-60 mix-blend-screen md:mix-blend-normal md:opacity-40"
        >
          <Image
            src="/hero-bg-v2.webp"
            alt="Hero Background"
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            quality={80}
            className="object-cover"
            priority
          />
        </m.div>
        <div className="absolute inset-0 bg-black/60 md:bg-black/50" />
        {/* Dynamic Interactive Gradient */}
        <m.div
          className="absolute inset-0 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000"
          style={{
            background: useMotionTemplate`radial-gradient(circle 600px at ${springX}px ${springY}px, rgba(25, 255, 181, 0.12), transparent 80%)`
          }}
        />
        {/* Static Fallback Gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(0,101,70,0.15),transparent_50%)] z-0 opacity-100 group-hover:opacity-0 transition-opacity duration-1000" />
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10 w-full flex flex-col items-center">
        <m.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          style={{ willChange: "transform, opacity" }}
          className="flex flex-col items-center w-full"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-gray-300 text-xs font-semibold tracking-widest uppercase mb-6 shadow-2xl hover:bg-white/10 transition-colors">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#19ffb5] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#19ffb5]"></span>
            </span>
            {t('hero.badge')}
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.1] mb-6 text-white max-w-4xl">
            {t('hero.title.1')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-gray-500">
              {t('hero.title.2')}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-base md:text-lg text-gray-400 mb-10 max-w-2xl leading-relaxed mx-auto font-light">
            {t('hero.description')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center w-full sm:w-auto">
            <Link
              href="#contact"
              className="px-8 py-3.5 text-black font-semibold text-base rounded-full transition-all duration-300 flex items-center justify-center gap-2 group shadow-[0_8px_32px_rgba(25,255,181,0.25)] hover:shadow-[0_12px_40px_rgba(25,255,181,0.35)] hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #006546 0%, #19ffb5 100%)' }}
            >
              {t('hero.cta.proposal')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              href="#portfolio"
              className="px-8 py-3.5 bg-transparent text-white font-semibold text-base rounded-full hover:bg-white/10 border border-white/20 transition-all duration-300 flex items-center justify-center"
            >
              {t('hero.cta.work')}
            </Link>
          </div>

          {/* Value Props / Stats */}
          <div className="mt-16 pt-8 border-t border-white/10 w-full max-w-3xl flex flex-wrap justify-center gap-6 md:gap-12 text-xs md:text-sm text-gray-400 font-medium">
            <m.div
              whileHover={{ scale: 1.05, color: '#19ffb5' }}
              className="flex items-center gap-3 transition-colors"
            >
              <div className="p-2 bg-white/5 rounded-lg border border-white/5 shadow-inner">
                <Code2 className="w-5 h-5 text-[#19ffb5]" />
              </div>
              <span className="tracking-wide">{t('hero.stat.roi')}</span>
            </m.div>
            <m.div
              whileHover={{ scale: 1.05, color: '#19ffb5' }}
              className="flex items-center gap-3 transition-colors"
            >
              <div className="p-2 bg-white/5 rounded-lg border border-white/5 shadow-inner">
                <Server className="w-5 h-5 text-[#19ffb5]" />
              </div>
              <span className="tracking-wide">{t('hero.stat.speed')}</span>
            </m.div>
            <m.div
              whileHover={{ scale: 1.05, color: '#19ffb5' }}
              className="flex items-center gap-3 transition-colors"
            >
              <div className="p-2 bg-white/5 rounded-lg border border-white/5 shadow-inner">
                <Zap className="w-5 h-5 text-[#19ffb5]" />
              </div>
              <span className="tracking-wide">{t('hero.stat.global')}</span>
            </m.div>
          </div>
        </m.div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
    </section>
  );
}
